import MomentUtils from "@date-io/moment";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { InfoOutlined } from "@material-ui/icons";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import {
    KeyboardDateTimePicker,
    MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { BsFiletypeHtml } from "react-icons/bs";
import { FaFileUpload } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { toastError } from "../../../utils";
import HtmlImportGuide from "../../../components/Common/HtmlImportGuide";
import { DownloadPdf } from "./downloadPdf";
import { UploadPDFModalCargoCargo } from "./uploadPdf";

/* -----------------------------------------------------------
   HELPER: UPDATE laytime fields inside correct cargo list
----------------------------------------------------------- */
const updateCargoOperationData = ({
    setValues,
    selectedOperationIndex,
    field,
    value,
    activeTab,
    setWarning = () => { },
    currentItemId = "",
}) => {
    setValues((prev) => {
        const newState = { ...prev };

        const cargoKey = activeTab === 0 ? "customerCargo" : "vendorCargo";
        if (!newState[cargoKey]) newState[cargoKey] = {};
        if (!newState[cargoKey].cargoList) newState[cargoKey].cargoList = [];

        const updatedCargoList = [...newState[cargoKey].cargoList];
        const currentItem = { ...updatedCargoList[selectedOperationIndex] };
        const currentLaytime = currentItem.laytimeStatement || {};

        const updatedData = { ...currentLaytime, [field]: value };

        /* Validate laytimeStarts/Stops */
        if (["laytimeStarts", "laytimeStops"].includes(field)) {
            const arrival = currentItem.arrivalTime ? new Date(currentItem.arrivalTime) : null;
            const departure = currentItem.departureTime ? new Date(currentItem.departureTime) : null;
            const newTime = value ? new Date(value) : null;

            if (newTime) {
                if (arrival && newTime < arrival) {
                    toastError(`${field} cannot be before arrival`);
                    return prev;
                }
                if (departure && newTime > departure) {
                    toastError(`${field} cannot be after departure`);
                    return prev;
                }
                if (field === "laytimeStops" && currentLaytime.laytimeStarts) {
                    if (newTime < new Date(currentLaytime.laytimeStarts)) {
                        toastError("Laytime end cannot be before start");
                        return prev;
                    }
                }
                if (field === "laytimeStarts" && currentLaytime.laytimeStops) {
                    if (newTime > new Date(currentLaytime.laytimeStops)) {
                        toastError("Laytime start cannot be after end");
                        return prev;
                    }
                }
            }
        }

        currentItem.laytimeStatement = calculateFormData(currentLaytime, updatedData);
        updatedCargoList[selectedOperationIndex] = currentItem;

        /* Clear warnings */
        setWarning((prevW) => {
            const newW = { ...prevW };
            if (newW[currentItemId]) {
                delete newW[currentItemId][field];
                if (Object.keys(newW[currentItemId]).length === 0) {
                    delete newW[currentItemId];
                }
            }
            return newW;
        });

        newState[cargoKey] = { ...newState[cargoKey], cargoList: updatedCargoList };
        return newState;
    });
};

/* -----------------------------------------------------------
   HELPER: UPDATE event table for correct cargo list
----------------------------------------------------------- */
const updateEventTable = (setValues, selectedOperationIndex, updatedEventTable, activeTab) => {
    setValues((prev) => {
        const newState = { ...prev };
        const cargoKey = activeTab === 0 ? "customerCargo" : "vendorCargo";

        if (!newState[cargoKey]) newState[cargoKey] = {};
        if (!newState[cargoKey].cargoList) newState[cargoKey].cargoList = [];

        const updatedCargoList = [...newState[cargoKey].cargoList];
        const currentItem = { ...updatedCargoList[selectedOperationIndex] };

        currentItem.laytimeStatement = {
            ...currentItem.laytimeStatement,
            eventTable: calculateTimeRows(updatedEventTable),
        };

        updatedCargoList[selectedOperationIndex] = currentItem;

        newState[cargoKey] = { ...newState[cargoKey], cargoList: updatedCargoList };
        return newState;
    });
};

/* -----------------------------------------------------------
   STYLES
----------------------------------------------------------- */
const useStyles = makeStyles((theme) => ({
    tableContainer: {
        margin: theme.spacing(3),
        maxWidth: "100%",
        overflowX: "auto",
    },
    datePicker: {
        "& .MuiInputBase-root": {
            padding: "5px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "#eef2fe",
            fontSize: "0.875rem",
        },
        "& .MuiInputBase-input": { padding: 0 },
    },
}));

/* -----------------------------------------------------------
   TIME CALCULATION HELPERS
----------------------------------------------------------- */
const calculateTimeRows = (rawRows) => {
    const updatedRows = rawRows.map((row, i, arr) => {
        const prevTo = i > 0 ? arr[i - 1].toTime : null;
        const nextTo = i < arr.length - 1 ? arr[i + 1].toTime : null;

        const fromTimeRaw = row.fromTime || prevTo;
        const toTimeRaw = row.toTime;
        const percent = Number(row.percentTimeToCount) || 0;

        const fromTime = fromTimeRaw && moment(fromTimeRaw).isValid() ? moment(fromTimeRaw) : null;
        const toTime = toTimeRaw && moment(toTimeRaw).isValid() ? moment(toTimeRaw) : null;

        let timeDuration = 0,
            timeUsed = 0;

        if (fromTime && toTime) {
            if (toTime.isBefore(fromTime)) {
                toastError("End cannot be before start");
                return row;
            }
            timeDuration = +toTime.diff(fromTime, "days", true).toFixed(3);
            timeUsed = +(timeDuration * (percent / 100)).toFixed(3);
        }

        return {
            ...row,
            fromTime: fromTime ? fromTime.toISOString() : "",
            toTime: toTime ? toTime.toISOString() : "",
            fromDay: fromTime ? fromTime.format("ddd") : "",
            toDay: toTime ? toTime.format("ddd") : "",
            timeDuration,
            timeUsed,
        };
    });

    let cumulative = 0;
    return updatedRows.map((r) => {
        cumulative += r.timeUsed || 0;
        return { ...r, cumulatedTimeUsed: +cumulative.toFixed(3) };
    });
};

const calculateFormData = (prevData, newData) => {
    const cargoQty = parseFloat(newData.cargoQty) || 0;
    const dischargeRate = parseFloat(newData.dischargingRate) || 1;

    const totalTimeAllowed = (cargoQty / dischargeRate).toFixed(3);
    return { ...prevData, ...newData, totalTimeAllowed };
};

const calculateSummaryDetails = (rows, data) => {
    let lastNot = 0;
    let lastDemurrage = null;
    const allowedDays = parseFloat(data.totalTimeAllowed || 0);

    for (const row of rows) {
        const diff = (Number(row.timeDuration) || 0) - (Number(row.timeUsed) || 0);
        lastNot = +diff.toFixed(3);

        if (row.fromTime && row.toTime) {
            const base = new Date(row.toTime);
            if (!lastDemurrage) {
                lastDemurrage = new Date(base.getTime() + allowedDays * 86400000);
            } else {
                lastDemurrage = new Date(lastDemurrage.getTime() + lastNot * 86400000);
            }
        }
    }

    return {
        vesselOnDemurrage: lastDemurrage ? lastDemurrage.toISOString() : null,
        lastNotCounted: lastNot,
        lastDemurrageBeginTime: lastDemurrage ? lastDemurrage.toISOString() : null,
    };
};

/* -----------------------------------------------------------
   STORE SUMMARY INTO CORRECT CARGO LIST
----------------------------------------------------------- */
const storeLaytimeSummary = (setValues, selectedIndex, summary, activeTab) => {
    setValues((prev) => {
        const newState = { ...prev };
        const cargoKey = activeTab === 0 ? "customerCargo" : "vendorCargo";

        const updatedList = [...newState[cargoKey].cargoList];
        const currentItem = { ...updatedList[selectedIndex] };

        const prevSummary = {
            vesselOnDemurrage: currentItem.laytimeStatement?.vesselOnDemurrage,
            lastNotCounted: currentItem.laytimeStatement?.lastNotCounted,
            lastDemurrageBeginTime: currentItem.laytimeStatement?.lastDemurrageBeginTime,
            totalDuration: currentItem.laytimeStatement?.totalDuration,
            totalTimeUsed: currentItem.laytimeStatement?.totalTimeUsed,
            totalTimeAllowed: currentItem.laytimeStatement?.totalTimeAllowed,
            difference: currentItem.laytimeStatement?.difference,
            demurrage: currentItem.laytimeStatement?.demurrage,
        };

        // 🔥 Prevent infinite re-renders — compare old and new summaries
        const isSame =
            JSON.stringify(prevSummary) === JSON.stringify(summary);

        if (isSame) return prev; // ← Do NOT update state if identical

        // Otherwise actually update state
        currentItem.laytimeStatement = {
            ...currentItem.laytimeStatement,
            ...summary,
        };

        updatedList[selectedIndex] = currentItem;

        newState[cargoKey] = { ...newState[cargoKey], cargoList: updatedList };
        return newState;
    });
};


/* -----------------------------------------------------------
   MAIN COMPONENT
----------------------------------------------------------- */
const LaytimeStatementCargoCargo = ({
    laytimeStatementDetails,
    isReadOnly = false,
    setValues,
    selectedOperationIndex,
    warning,
    currentItemId,
    activeTab,
    setWarning,
    onDownloadPdf,
}) => {
    const classes = useStyles();
    const [openUploadPDF, setOpenUploadPDF] = useState(false);
    const [openUploadHTML, setOpenUploadHTML] = useState(false);
    const inputFileRef = useRef(null);

    const onButtonClick = () => {
        setOpenUploadHTML(true);
    };

    const isDischarging = useRef(false);
    useEffect(() => {
        isDischarging.current = (laytimeStatementDetails?.operation?.toLowerCase() || "").trim() === "discharging";
    }, [laytimeStatementDetails.operation]);
    /* Summary calculation */
    const laytimeSummary = useMemo(() => {
        const totalDuration = laytimeStatementDetails.eventTable?.reduce(
            (s, r) => s + (parseFloat(r.timeDuration) || 0),
            0
        );
        const totalTimeUsed = laytimeStatementDetails.eventTable?.reduce(
            (s, r) => s + (parseFloat(r.timeUsed) || 0),
            0
        );
        const allowed = parseFloat(laytimeStatementDetails.totalTimeAllowed) || 0;
        const diff = totalTimeUsed - allowed;
        const demurrage =
            diff > 0
                ? diff * (parseFloat(laytimeStatementDetails.despatchRate) || 0)
                : diff * allowed;

        return { totalDuration, totalTimeUsed, totalTimeAllowed: allowed, difference: diff, demurrage };
    }, [laytimeStatementDetails]);

    /* vesselOnDemurrage auto update */
    const summaryDetails = useMemo(
        () => calculateSummaryDetails(laytimeStatementDetails.eventTable || [], laytimeStatementDetails),
        [laytimeStatementDetails]
    );

    useEffect(() => {
        if (summaryDetails.vesselOnDemurrage) {
            updateCargoOperationData({
                setValues,
                selectedOperationIndex,
                field: "vesselOnDemurrage",
                value: summaryDetails.vesselOnDemurrage,
                activeTab,
                setWarning,
                currentItemId,
            });
        }
    }, [summaryDetails.vesselOnDemurrage]);

    /* Event table field editing */
    const handleInputChange = useCallback(
        (id, field, value) => {
            const updated = laytimeStatementDetails.eventTable.map((r) =>
                r.id === id ? { ...r, [field]: value } : r
            );
            updateEventTable(setValues, selectedOperationIndex, updated, activeTab);
        },
        [laytimeStatementDetails.eventTable]
    );

    /* DATETIME PICKER handler */
    const handleDateChange = useCallback(
        (id, field, date, index) => {
            const layStart = laytimeStatementDetails.laytimeStarts;
            const departure = laytimeStatementDetails.departureTime;

            if (!layStart) {
                toastError("Please set laytime start and end first");
                return;
            }

            const toMoment = (v) => (v ? moment(v) : null);
            const newTime = toMoment(date);
            const start = toMoment(layStart);
            const depart = toMoment(departure);

            const rows = laytimeStatementDetails.eventTable || [];
            const updated = [...rows];
            const row = { ...updated[index] };

            /* Validation */
            if (field === "fromTime" && newTime) {
                if (start && newTime.isBefore(start)) return toastError("From cannot be before start");
                if (depart && newTime.isAfter(depart)) return toastError("From cannot be after departure");
            }

            if (field === "toTime" && newTime) {
                if (start && newTime.isBefore(start)) return toastError("To cannot be before start");
                if (depart && newTime.isAfter(depart)) return toastError("To cannot be after departure");
                if (row.fromTime && newTime.isBefore(moment(row.fromTime)))
                    return toastError("To cannot be before From");

                if (index < updated.length - 1) {
                    const next = moment(updated[index + 1].toTime);
                    if (next.isValid() && newTime.isAfter(next))
                        return toastError("To cannot exceed next row To");
                }
            }

            row[field] = date;
            updated[index] = row;

            /* adjust next row */
            if (field === "toTime" && index < updated.length - 1) {
                updated[index + 1] = { ...updated[index + 1], fromTime: date };
            }

            updateEventTable(setValues, selectedOperationIndex, updated, activeTab);
        },
        [laytimeStatementDetails]
    );

    /* ADD ROW */
    const addRow = useCallback(() => {
        const list = laytimeStatementDetails.eventTable || [];
        const newId = list.length > 0 ? Math.max(...list.map((r) => r.id)) + 1 : 1;

        const last = list[list.length - 1];
        const newRow = {
            id: newId,
            fromTime: last?.toTime || null,
            fromDay: last?.toDay || "",
            toTime: null,
            toDay: "",
            event: "",
            timeDuration: 0,
            percentTimeToCount: 100,
            timeUsed: 0,
            cumulatedTimeUsed: 0,
            comments: "",
        };

        updateEventTable(setValues, selectedOperationIndex, [...list, newRow], activeTab);
    }, [laytimeStatementDetails.eventTable]);

    /* DELETE ROW */
    const deleteRow = useCallback(
        (id) => {
            const updated = laytimeStatementDetails.eventTable.filter((r) => r.id !== id);
            updateEventTable(setValues, selectedOperationIndex, updated, activeTab);
        },
        [laytimeStatementDetails.eventTable]
    );

    /* MAIN FIELD UPDATE */
    const handleChange = useCallback(
        (field, value) => {
            updateCargoOperationData({
                setValues,
                selectedOperationIndex,
                field,
                value,
                setWarning,
                currentItemId,
                activeTab,
            });
        },
        [selectedOperationIndex]
    );

    /* FILE PARSER (unchanged but FIXED references) */
    function getInputValue(doc, id) {
        const el = doc.getElementById(id);
        if (!el) return "";
        return el.value ?? el.textContent ?? "";
    }
    function getDateValue(doc, id) {
        const el = doc.getElementById(id);
        if (!el) return null;
        const raw = el.value || el.textContent || "";
        const d = new Date(raw);
        return isNaN(d.getTime()) ? null : d;
    }
    const getNumberValue = (doc, id) => {
        const raw = getInputValue(doc, id);
        const n = parseFloat(raw);
        return isNaN(n) ? null : n;
    };
    function getSpanText(doc, id) {
        const el = doc.getElementById(id);
        return el ? (el.textContent || "").trim() : "";
    }

    const parseHtmlFile = async (file) => {
        const text = await file.text();
        const doc = new DOMParser().parseFromString(text, "text/html");

        const vesselData = {
            vesselName: getInputValue(doc, "vesselName"),
            cargoDesc: getInputValue(doc, "cargoDesc"),
            billOfLadingDate: getDateValue(doc, "billOfLadingDate"),
            voyageNo: getInputValue(doc, "voyageNo"),
            charterer: getInputValue(doc, "charterer"),
            laytimePort: getInputValue(doc, "laytimePort"),
            operation: getInputValue(doc, "operation"),
            shipmentId: getInputValue(doc, "shipmentId"),
            cargoQty: getNumberValue(doc, "cargoQty"),
            dischargingRate: getNumberValue(doc, "dischargingRate"),
            dischargingTerms: getInputValue(doc, "dischargingTerms"),
            turnTimeAllowed: getNumberValue(doc, "turnTimeAllowed"),
            turnTimeClause: getInputValue(doc, "turnTimeClause"),
            demurrageRate: getNumberValue(doc, "demurrageRate"),
            despatchRate: getNumberValue(doc, "despatchRate"),
            totalTimeAllowed: getNumberValue(doc, "totalTimeAllowed"),
            arrivalTime: getDateValue(doc, "arrivalTime"),
            norTendered: getDateValue(doc, "norTendered"),
            norTerms: getInputValue(doc, "norTerms"),
            laytimeStarts: getDateValue(doc, "laytimeStarts"),
            commencedDischarging: getDateValue(doc, "commencedDischarging"),
            completedDischarging: getDateValue(doc, "completedDischarging"),
            laytimeStops: getDateValue(doc, "laytimeStops"),
            vesselOnDemurrage: getDateValue(doc, "vesselOnDemurrage"),
            departureTime: getDateValue(doc, "departureTime"),
        };

        const eventTableBody = doc.querySelector("#eventTableBody");
        let eventTable = [];

        if (eventTableBody) {
            eventTable = [...eventTableBody.querySelectorAll("tr")]
                .map((tr) => {
                    const tds = tr.querySelectorAll("td");
                    if (tds.length < 10) return null;
                    return {
                        fromTime: tds[1].querySelector("input")?.value || "",
                        fromDay: tds[2].textContent.trim(),
                        toTime: tds[3].querySelector("input")?.value || "",
                        toDay: tds[4].textContent.trim(),
                        event: tds[5].querySelector("input")?.value || "",
                        timeDuration: tds[6].textContent.trim(),
                        percentTimeToCount: tds[7].querySelector("input")?.value || "",
                        timeUsed: tds[8].textContent.trim(),
                        cumulatedTimeUsed: tds[9].textContent.trim(),
                        comments: tds[10]?.querySelector("input")?.value || "",
                    };
                })
                .filter(Boolean);
        }

        const summary = {
            totalDuration: getSpanText(doc, "totalDuration"),
            totalTimeUsed: getSpanText(doc, "totalTimeUsed"),
            totalTimeAllowedSummary: getSpanText(doc, "totalTimeAllowedSummary"),
            difference: getSpanText(doc, "difference"),
            demurrage: getSpanText(doc, "demurrage"),
        };

        return { ...vesselData, eventTable, summary };
    };

    const handleFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const data = await parseHtmlFile(file);

            setValues((prev) => {
                const newState = { ...prev };

                const cargoKey = activeTab === 0 ? "customerCargo" : "vendorCargo";

                if (!newState[cargoKey]) newState[cargoKey] = {};
                if (!newState[cargoKey].cargoList) newState[cargoKey].cargoList = [];

                const updatedCargoList = [...newState[cargoKey].cargoList];
                const currentItem = { ...updatedCargoList[selectedOperationIndex] };

                const currentLaytimeStatement = currentItem?.laytimeStatement;
                const arrivalTime = moment(currentItem?.arrivalTime);
                const departureTime = moment(currentItem?.departureTime);

                if (currentLaytimeStatement) {
                    const laytimeStart = moment(data?.laytimeStarts);
                    const laytimeStop = moment(data?.laytimeStops);
                    const eventTable = data?.eventTable;

                    const newWarnings = { ...warning };
                    const itemId = currentItem?._id;
                    let toastWarnings = [];

                    if (!newWarnings[itemId]) {
                        newWarnings[itemId] = {};
                    }

                    if (laytimeStart.isBefore(arrivalTime)) {
                        newWarnings[itemId].laytimeStarts =
                            "Laytime Start cannot be earlier than Arrival Time";
                        toastWarnings.push("Laytime Start cannot be earlier than Arrival Time");
                    } else if (laytimeStart.isAfter(departureTime)) {
                        newWarnings[itemId].laytimeStarts =
                            "Laytime Start cannot be later than Departure Time";
                        toastWarnings.push("Laytime Start cannot be later than Departure Time");
                    }

                    if (laytimeStop.isBefore(arrivalTime)) {
                        newWarnings[itemId].laytimeStops =
                            "Laytime Stop cannot be earlier than Arrival Time";
                        toastWarnings.push("Laytime Stop cannot be earlier than Arrival Time");
                    } else if (laytimeStop.isAfter(departureTime)) {
                        newWarnings[itemId].laytimeStops =
                            "Laytime Stop cannot be later than Departure Time";
                        toastWarnings.push("Laytime Stop cannot be later than Departure Time");
                    }

                    eventTable.forEach((evt, idx) => {
                        const fromM = moment(evt.fromTime);
                        const toM = moment(evt.toTime);
                        let rowWarns = {};

                        if (fromM.isBefore(arrivalTime)) {
                            rowWarns.fromTime = "Event ‘fromTime’ cannot be before Arrival Time";
                        } else if (fromM.isAfter(departureTime)) {
                            rowWarns.fromTime = "Event ‘fromTime’ cannot be after Departure Time";
                        }

                        if (toM.isBefore(arrivalTime)) {
                            rowWarns.toTime = "Event ‘toTime’ cannot be before Arrival Time";
                        } else if (toM.isAfter(departureTime)) {
                            rowWarns.toTime = "Event ‘toTime’ cannot be after Departure Time";
                        }

                        if (Object.keys(rowWarns).length > 0) {
                            if (!newWarnings[itemId].eventTable) {
                                newWarnings[itemId].eventTable = {};
                            }
                            newWarnings[itemId].eventTable[idx] = rowWarns;

                            Object.values(rowWarns).forEach((msg) =>
                                toastWarnings.push(`Row ${idx + 1}: ${msg}`)
                            );
                        }
                    });

                    if (toastWarnings.length > 0) {
                        const formattedToast = (
                            <div>
                                <strong className="text-rose-600">⚠️ Validation Warning</strong>
                                <ul className="mt-1 list-disc list-inside text-sm text-rose-700">
                                    {toastWarnings.map((msg, i) => (
                                        <li key={i}>{msg}</li>
                                    ))}
                                </ul>
                            </div>
                        );
                        toast.error(formattedToast, {
                            autoClose: false,
                            position: "bottom-left",
                            className:
                                "max-w-sm sm:max-w-md md:max-w-2xl w-[450px] text-black border border-rose-300 rounded-md p-4 bg-rose-100 shadow-lg",
                            bodyClassName: "overflow-x-auto",
                        });
                    }

                    setWarning(newWarnings);
                }

                const {
                    vesselName,
                    arrivalTime: parsedArrivalTime,
                    departureTime: parsedDepartureTime,
                    cargoDesc,
                    voyageNo,
                    shipmentId,
                    operation,
                    laytimePort,
                    charterer,
                    billOfLadingDate,
                    ...rest
                } = data;

                currentItem.laytimeStatement = rest;

                updatedCargoList[selectedOperationIndex] = currentItem;

                newState[cargoKey] = {
                    ...newState[cargoKey],
                    cargoList: updatedCargoList,
                };

                return newState;
            });
        } catch (err) {
            toastError("Failed to parse file.");
        }
    };

    const renderDatePicker = useCallback(
        (field, label, warning) => {
            const rawVal = laytimeStatementDetails[field];
            const validVal = rawVal && moment(rawVal).isValid() ? moment(rawVal) : null;
            return (
                <tr className="hover:bg-[#ffffcc]/60">
                    <td className="w-1/2 border border-slate-300 bg-slate-50 px-2 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
                        {label}
                    </td>
                    <td className="w-1/2 border border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                        <div className="flex items-center w-full h-[19px]">
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                                <Tooltip
                                    title={
                                        <span style={{ fontSize: "0.73rem" }}>
                                            {validVal
                                                ? `Selected: ${validVal.format("DD/MM/YYYY HH:mm")}`
                                                : "Select date and time"}
                                        </span>
                                    }
                                    placement="top"
                                    arrow
                                >
                                    <KeyboardDateTimePicker
                                        size="small"
                                        variant="dialog"
                                        ampm={false}
                                        margin="none"
                                        style={{ width: "100%", height: "19px", margin: 0, padding: 0 }}
                                        className="laytime-datepicker w-full bg-transparent h-[19px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[19px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[19px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[19px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-1.5 [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-normal [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-0 [&_.MuiInputAdornment-root]:!h-[19px] [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[19px] [&_.MuiIconButton-root]:!w-[19px] [&_.MuiSvgIcon-root]:!w-3 [&_.MuiSvgIcon-root]:!h-3 [&_.MuiSvgIcon-root]:!text-[10px]"
                                        format="DD/MM/YYYY HH:mm"
                                        value={validVal}
                                        onChange={(date) => handleChange(field, date)}
                                        placeholder="DD/MM/YYYY HH:mm"
                                        inputProps={{
                                            style: {
                                                backgroundColor: "transparent",
                                                height: "19px",
                                                minHeight: "19px",
                                                maxHeight: "19px",
                                                padding: "0 4px",
                                                fontSize: "11px",
                                                lineHeight: "normal",
                                            },
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            disableUnderline: true,
                                            style: {
                                                fontSize: "11px",
                                                padding: "0 2px",
                                                height: "19px",
                                                minHeight: "19px",
                                                maxHeight: "19px",
                                                backgroundColor: "transparent",
                                            },
                                        }}
                                        error={false}
                                        helperText={null}
                                        FormHelperTextProps={{ style: { display: "none" } }}
                                        onError={() => { }}
                                        keyboardIcon={<CalendarTodayIcon color="action" style={{ fontSize: "0.55rem" }} />}
                                        KeyboardButtonProps={{ style: { padding: 0, margin: 0 } }}
                                    />
                                </Tooltip>
                            </MuiPickersUtilsProvider>
                            {warning && (
                                <Tooltip title={warning} placement="top" arrow>
                                    <InfoOutlined color="error" style={{ fontSize: "0.85rem", marginLeft: "2px" }} />
                                </Tooltip>
                            )}
                        </div>
                    </td>
                </tr>
            );
        },
        [laytimeStatementDetails, handleChange]
    );

    const renderTabelDatePicker = useCallback(
        (id, field, value, index) => {
            const validVal = value && moment(value).isValid() ? moment(value) : null;
            return (
                <MuiPickersUtilsProvider utils={MomentUtils}>
                    <KeyboardDateTimePicker
                        value={validVal}
                        onChange={(date) => handleDateChange(id, field, date, index)}
                        format="DD/MM/YYYY HH:mm"
                        placeholder="DD/MM/YYYY HH:mm"
                        ampm={false}
                        margin="none"
                        style={{ width: "100%", height: "19px", margin: 0, padding: 0 }}
                        className="laytime-datepicker w-full bg-transparent h-[19px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[19px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[19px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[19px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-1.5 [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-[19px] [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-0 [&_.MuiInputAdornment-root]:!h-[19px] [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[19px] [&_.MuiIconButton-root]:!w-[19px] [&_.MuiSvgIcon-root]:!w-3 [&_.MuiSvgIcon-root]:!h-3 [&_.MuiSvgIcon-root]:!text-[10px]"
                        inputProps={{
                            style: {
                                backgroundColor: "transparent",
                                height: "19px",
                                minHeight: "19px",
                                maxHeight: "19px",
                                padding: "0 4px",
                                fontSize: "11px",
                                lineHeight: "19px",
                            },
                        }}
                        InputProps={{
                            readOnly: true,
                            disableUnderline: true,
                            style: {
                                fontSize: "11px",
                                padding: "0 2px",
                                height: "19px",
                                minHeight: "19px",
                                maxHeight: "19px",
                                backgroundColor: "transparent",
                            },
                        }}
                        error={false}
                        helperText={null}
                        FormHelperTextProps={{
                            style: { display: "none" },
                        }}
                        onError={() => { }}
                        keyboardIcon={
                            <CalendarTodayIcon color="action" style={{ fontSize: "0.55rem" }} />
                        }
                        KeyboardButtonProps={{ style: { padding: 0, margin: 0 } }}
                    />
                </MuiPickersUtilsProvider>
            );
        },
        [handleDateChange]
    );

    const renderInput = useCallback(
        (field, label, type = "text", isDisabled) => {
            const rawValue = laytimeStatementDetails[field];
            let displayValue = rawValue || "";
            if (
                field === "vesselOnDemurrage" ||
                field === "arrivalTime" ||
                field === "departureTime"
            ) {
                displayValue =
                    rawValue && moment(rawValue).isValid()
                        ? moment(rawValue).format("DD/MM/YYYY HH:mm")
                        : "-";
            }

            return (
                <tr className="hover:bg-[#ffffcc]/60">
                    <td className="w-1/2 border border-slate-300 bg-slate-50 px-2 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
                        {label}
                    </td>
                    <td className={`w-1/2 border border-slate-300 p-0 align-middle h-[19px] ${isDisabled ? "bg-slate-100" : "bg-[#ffffd6]"}`}>
                        <input
                            type={type}
                            value={displayValue}
                            onChange={(e) => handleChange(field, e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            disabled={isDisabled}
                            className={`w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none border-none font-medium ${isDisabled
                                ? "bg-slate-100 text-slate-700 cursor-not-allowed"
                                : "bg-[#ffffd6] text-slate-900 focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                }`}
                        />
                    </td>
                </tr>
            );
        },
        [laytimeStatementDetails, handleChange]
    );

    return (
        <div className="space-y-1">
            {!isReadOnly && (
                <div className="flex items-center justify-end gap-1.5 mb-1">
                    <DownloadPdf
                        isLaytimeStatement
                        pdfData={{
                            ...laytimeStatementDetails,
                            ...laytimeSummary,
                        }}
                        onDownloadPdf={onDownloadPdf}
                    />
                    <button
                        onClick={() => setOpenUploadPDF(true)}
                        className="flex items-center gap-1.5 px-2.5 py-0.5 h-[22px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none cursor-pointer hover:bg-[#0c2f50] transition-colors"
                    >
                        <FaFileUpload size={13} />
                        <span>Upload PDF</span>
                    </button>
                    <input
                        ref={inputFileRef}
                        type="file"
                        accept=".html,.htm"
                        style={{ display: "none" }}
                            onChange={(event) => { setOpenUploadHTML(false); handleFile(event); }}
                    />
                    <button
                        onClick={onButtonClick}
                        className="flex items-center gap-1.5 px-2.5 py-0.5 h-[22px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none cursor-pointer hover:bg-[#0c2f50] transition-colors"
                    >
                        <BsFiletypeHtml size={13} />
                        <span>Upload HTML</span>
                    </button>
                </div>
            )}
            {laytimeStatementDetails.vesselName && (
                <div className="text-center mb-1">
                    <h4 className="font-medium text-[11px] uppercase tracking-wider text-[#313C4C] m-0">
                        Laytime Statement for{" "}
                        <span className="text-slate-900 font-semibold">
                            {laytimeStatementDetails.vesselName}
                        </span>{" "}
                        B/L Dated{" "}
                        <span className="text-slate-900 font-semibold">
                            {laytimeStatementDetails.billOfLadingDate &&
                                moment(laytimeStatementDetails.billOfLadingDate).isValid()
                                ? moment(laytimeStatementDetails.billOfLadingDate).format("DD-MMM-YYYY")
                                : "N/A"}
                        </span>
                        : {laytimeStatementDetails.shipmentId || "N/A"} For{" "}
                        <span className="text-slate-900 font-semibold">
                            {laytimeStatementDetails.laytimePort || "N/A"}
                        </span>{" "}
                        - {laytimeStatementDetails.operation || "N/A"}
                    </h4>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5 mb-1.5">
                {/* First Column - Vessel Info */}
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-900 capitalize tracking-wider text-center">
                        Vessel Info
                    </div>
                    <table className="w-full text-left border-collapse table-fixed">
                        <tbody>
                            {renderInput("vesselName", "Vessel Name", "text", true)}
                            {renderInput("cargoDesc", "Cargo Desc", "text", true)}
                            {renderInput("billOfLadingDate", "B/L Date", "date", true)}
                            {renderInput("voyageNo", "Voyage No", "text", true)}
                            {renderInput("charterer", "Charterer", "text", true)}
                            {renderInput("port", "Laytime for Port", "text", true)}
                            {renderInput("operation", "Operation", "text", true)}
                            {renderInput("shipmentId", "Shipment ID", "text", true)}
                        </tbody>
                    </table>
                </div>

                {/* Second Column - Terms & Rates */}
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-900 capitalize tracking-wider text-center">
                        Terms & Rates
                    </div>
                    <table className="w-full text-left border-collapse table-fixed">
                        <tbody>
                            {renderInput("cargoQty", "Cargo Quantity (MT)", "number")}
                            {renderInput(
                                "dischargingRate",
                                isDischarging.current ? "Discharging Rate" : "Loading Rate",
                                "number"
                            )}
                            {renderInput(
                                "dischargingTerms",
                                isDischarging.current ? "Discharging Terms" : "Loading Terms",
                                "text"
                            )}
                            {renderInput("turnTimeAllowed", "Turn Time Allowed", "number")}
                            {renderInput("turnTimeClause", "Turn Time Clause", "text")}
                            {renderInput("demurrageRate", "Demurrage Rate ($/day)", "number")}
                            {renderInput("despatchRate", "Despatch Rate ($/day)", "number")}
                            {renderInput(
                                "totalTimeAllowed",
                                "Total Time Allowed",
                                "number",
                                true
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Third Column - Time Marks */}
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-900 capitalize tracking-wider text-center">
                        Time Marks
                    </div>
                    <table className="w-full text-left border-collapse table-fixed">
                        <tbody>
                            {renderInput("arrivalTime", "Arrival time", "text", true)}
                            {renderDatePicker("norTendered", "NOR Tendered")}
                            {renderInput("norTerms", "NOR Terms", "text")}
                            {renderDatePicker(
                                "laytimeStarts",
                                "Laytime Starts",
                                warning?.[currentItemId]?.laytimeStarts || ""
                            )}
                            {renderDatePicker(
                                "commencedDischarging",
                                isDischarging.current ? "Commenced Discharging" : "Commenced Loading"
                            )}
                            {renderDatePicker(
                                "completedDischarging",
                                isDischarging.current ? "Completed Discharging" : "Completed Loading"
                            )}
                            {renderDatePicker(
                                "laytimeStops",
                                "Laytime Stops",
                                warning?.[currentItemId]?.laytimeStops || ""
                            )}
                            {renderInput(
                                "vesselOnDemurrage",
                                "Vessel on Demurrage",
                                "text",
                                true
                            )}
                            {renderInput("departureTime", "Departure Time", "text", true)}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Events Table Section */}
            <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs mb-1.5">
                <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 capitalize tracking-wider">
                    Events Table
                </div>
                {(warning?.[currentItemId]?.laytimeStarts ||
                    warning?.[currentItemId]?.laytimeStops) && (
                        <div className="px-2 py-0.5 border-b border-slate-300 bg-rose-50 text-rose-700 text-[11px] flex items-center gap-1.5">
                            <InfoOutlined color="error" style={{ fontSize: "0.85rem" }} />
                            <div>
                                The <strong>Laytime Start</strong> or{" "}
                                <strong>Laytime Stop</strong> values are invalid. This may cause{" "}
                                <strong>incorrect event details</strong> to be shown in the
                                table.
                            </div>
                        </div>
                    )}

                <div className="overflow-x-auto scroll-thin w-full">
                    <table className="w-full text-left border-collapse border-b border-slate-300 table-fixed min-w-[1050px]">
                        <thead>
                            <tr>
                                {!isReadOnly && (
                                    <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[32px]">
                                        Add
                                    </th>
                                )}
                                <th className="px-1 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[115px]">
                                    From Time
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[35px]">
                                    Day
                                </th>
                                <th className="px-1 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[125px]">
                                    To Time
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[35px]">
                                    Day
                                </th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle min-w-[160px]">
                                    Event
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                                    Duration (days)
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                                    % Time to Count
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                                    Time Used (days)
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                                    Cumulated Time
                                </th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle min-w-[130px]">
                                    Comments
                                </th>
                                {!isReadOnly && (
                                    <th className="px-1 py-0.5 text-center border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[52px]">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {laytimeStatementDetails.eventTable?.length > 0 ? (
                                laytimeStatementDetails.eventTable.map((row, index) => {
                                    const rowWarnings =
                                        warning?.[currentItemId]?.eventTable?.[index] || {};
                                    const isAfterDeparture =
                                        (row.fromTime &&
                                            laytimeStatementDetails.vesselOnDemurrage &&
                                            new Date(row.fromTime) >
                                            new Date(laytimeStatementDetails.vesselOnDemurrage)) ||
                                        (row.toTime &&
                                            laytimeStatementDetails.vesselOnDemurrage &&
                                            new Date(row.toTime) >
                                            new Date(laytimeStatementDetails.vesselOnDemurrage));
                                    return (
                                        <tr key={row.id} className="hover:bg-[#ffffcc]/60">
                                            {!isReadOnly && (
                                                <td className="border border-slate-300 p-0 text-center bg-slate-50 align-middle">
                                                    <div className="flex items-center justify-center h-[19px]">
                                                        <button
                                                            onClick={addRow}
                                                            className="p-0 text-blue-600 hover:text-blue-800 transition cursor-pointer"
                                                            title="Add row"
                                                        >
                                                            <BiPlus style={{ width: "9px", height: "9px", minWidth: "9px", minHeight: "9px" }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                            <td className={`border border-slate-300 p-0 align-middle ${isAfterDeparture ? "bg-rose-50 text-rose-700 font-semibold" : "bg-slate-50"}`}>
                                                <div className="h-[19px] px-1.5 flex items-center gap-1 text-[11px] font-medium text-slate-800 whitespace-nowrap">
                                                    <span>
                                                        {row.fromTime && moment(row.fromTime).isValid()
                                                            ? moment(row.fromTime).format("DD/MM/YYYY HH:mm")
                                                            : "-"}
                                                    </span>
                                                    {rowWarnings.fromTime && (
                                                        <Tooltip
                                                            placement="top"
                                                            arrow
                                                            title={
                                                                <span style={{ fontSize: "0.675rem" }}>
                                                                    {rowWarnings.fromTime}
                                                                </span>
                                                            }
                                                        >
                                                            <span className="text-rose-500" title={rowWarnings.fromTime}>
                                                                <InfoOutlined color="error" style={{ fontSize: "0.85rem" }} />
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="border border-slate-300 p-0 text-center bg-slate-50 align-middle">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-600 uppercase">
                                                    {row.fromDay || "-"}
                                                </div>
                                            </td>

                                            <td className={`border border-slate-300 p-0 align-middle ${isAfterDeparture ? "bg-rose-50" : "bg-[#ffffd6]"}`}>
                                                <div className="flex items-center gap-1 w-full">
                                                    {renderTabelDatePicker(
                                                        row.id,
                                                        "toTime",
                                                        row.toTime,
                                                        index
                                                    )}
                                                    {rowWarnings.toTime && (
                                                        <Tooltip
                                                            title={
                                                                <span style={{ fontSize: "0.675rem" }}>
                                                                    {rowWarnings.toTime}
                                                                </span>
                                                            }
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <InfoOutlined color="error" style={{ fontSize: "0.85rem" }} />
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="border border-slate-300 p-0 text-center bg-slate-50 align-middle">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-600 uppercase">
                                                    {row.toDay || "-"}
                                                </div>
                                            </td>

                                            <td className="border border-slate-300 p-0 align-middle bg-[#ffffd6]">
                                                <input
                                                    value={row.event || ""}
                                                    onChange={(e) =>
                                                        handleInputChange(row.id, "event", e.target.value)
                                                    }
                                                    className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-500 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-gray-400"
                                                    placeholder="Enter event..."
                                                />
                                            </td>

                                            <td className="border border-slate-300 p-0 align-middle bg-slate-100 text-center">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                                                    {row.timeDuration !== undefined && row.timeDuration !== null ? row.timeDuration : "0"}
                                                </div>
                                            </td>

                                            <td className="border border-slate-300 p-0 align-middle bg-[#ffffd6] text-center">
                                                <input
                                                    type="number"
                                                    value={row.percentTimeToCount !== undefined ? row.percentTimeToCount : ""}
                                                    onWheel={(e) => e.target.blur()}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            row.id,
                                                            "percentTimeToCount",
                                                            e.target.value
                                                        )
                                                    }
                                                    min={0}
                                                    max={100}
                                                    className="w-full h-[19px] text-[11px] px-1.5 py-0 text-center block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-500 bg-[#ffffd6] text-slate-900 border-none font-medium"
                                                    placeholder="0"
                                                />
                                            </td>

                                            <td className="border border-slate-300 p-0 align-middle bg-slate-100 text-center">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                                                    {row.timeUsed !== undefined && row.timeUsed !== null ? row.timeUsed : "0"}
                                                </div>
                                            </td>

                                            <td className="border border-slate-300 p-0 align-middle bg-slate-100 text-center">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                                                    {row.cumulatedTimeUsed !== undefined && row.cumulatedTimeUsed !== null ? row.cumulatedTimeUsed : "0"}
                                                </div>
                                            </td>

                                            <td className="border border-slate-300 p-0 align-middle bg-[#ffffd6]">
                                                <input
                                                    value={row.comments || ""}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            row.id,
                                                            "comments",
                                                            e.target.value
                                                        )
                                                    }
                                                    className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-500 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-gray-400"
                                                    placeholder="Comments..."
                                                />
                                            </td>

                                            {!isReadOnly && (
                                                <td className="border border-slate-300 p-0 text-center bg-slate-50 align-middle">
                                                    <div className="flex items-center justify-center h-[19px]">
                                                        <button
                                                            onClick={() => deleteRow(row.id)}
                                                            className="p-0 text-rose-600 hover:text-rose-800 transition cursor-pointer"
                                                            title="Delete row"
                                                        >
                                                            <FiTrash2 style={{ width: "9px", height: "9px", minWidth: "9px", minHeight: "9px" }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td
                                        colSpan={12}
                                        className="px-4 py-2 text-center text-slate-400 italic text-[11px] border border-slate-300"
                                    >
                                        {!isReadOnly ? (
                                            <div className="flex flex-col items-center justify-center gap-1.5">
                                                <span>No event logs added yet.</span>
                                                <button
                                                    onClick={addRow}
                                                    className="px-2.5 py-0.5 text-[11px] font-medium bg-[#313C4C] text-white hover:bg-[#0d508f] transition cursor-pointer flex items-center gap-1"
                                                >
                                                    <BiPlus size={14} />
                                                    <span>Add First Row</span>
                                                </button>
                                            </div>
                                        ) : (
                                            "No Data"
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="mt-1 flex w-full justify-end">
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs w-full md:w-80">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 capitalize tracking-wider text-center">
                        Laytime Summary
                    </div>
                    <table className="w-full text-left border-collapse table-fixed">
                        <tbody>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-800 align-middle">
                                    Total Duration:
                                </td>
                                <td className="w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle">
                                    {laytimeSummary?.totalDuration?.toFixed(5) || 0} days
                                </td>
                            </tr>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-800 align-middle">
                                    Total Time Used:
                                </td>
                                <td className="w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle">
                                    {laytimeSummary?.totalTimeUsed?.toFixed(5) || 0} days
                                </td>
                            </tr>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-800 align-middle">
                                    Total Time Allowed:
                                </td>
                                <td className="w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle">
                                    {laytimeSummary?.totalTimeAllowed?.toFixed(5) || 0} days
                                </td>
                            </tr>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 align-middle">
                                    Difference:
                                </td>
                                <td className={`w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-right bg-[#ffffd6] align-middle ${laytimeSummary?.difference > 0 ? "text-emerald-700" : "text-rose-700"
                                    }`}>
                                    {laytimeSummary?.difference?.toFixed(5) || 0} days
                                </td>
                            </tr>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 align-middle">
                                    Demurrage / Despatch:
                                </td>
                                <td className={`w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-right bg-[#ffffd6] align-middle ${laytimeSummary?.difference > 0 ? "text-emerald-700" : "text-rose-700"
                                    }`}>
                                    {laytimeSummary?.demurrage?.toFixed(3) || 0}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <UploadPDFModalCargoCargo
                open={openUploadPDF}
                handleClose={() => setOpenUploadPDF(false)}
                title="Import Laytime Statement from PDF"
                isLaytimeStatement={true}
                selectedOperationIndex={selectedOperationIndex}
                setState={setValues}
                warning={warning}
                setWarning={setWarning}
                activeTab={activeTab}
            />
            <HtmlImportGuide open={openUploadHTML} onClose={() => setOpenUploadHTML(false)} onChooseFile={() => inputFileRef.current?.click()} kind="laytime" />
        </div>
    );
};

export default LaytimeStatementCargoCargo;
